<template>
  <div class="contract-template-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>合同模板管理</h2>
      <el-button type="primary" @click="handleAdd">
        <el-icon><Plus /></el-icon>
        新建模板
      </el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input v-model="searchParams.keyword" placeholder="搜索模板名称" clearable style="width: 200px" @keyup.enter="loadData" />
      <el-select v-model="searchParams.isActive" placeholder="状态" clearable style="width: 120px">
        <el-option label="启用" :value="true" />
        <el-option label="禁用" :value="false" />
      </el-select>
      <el-button type="primary" @click="loadData">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="tableData" v-loading="loading" stripe>
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="模板名称" min-width="150" />
      <el-table-column prop="code" label="模板代码" width="150" />
      <el-table-column prop="signProvider" label="签名服务商" width="120">
        <template #default="{ row }">
          <el-tag :type="row.signProvider === 'self' ? 'success' : 'primary'">
            {{ signProviderMap[row.signProvider] || row.signProvider }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="isActive" label="状态" width="100">
        <template #default="{ row }">
          <el-switch v-model="row.isActive" @change="handleStatusChange(row)" />
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column prop="createdAt" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="handleEdit(row)">编辑</el-button>
          <el-button link type="primary" @click="handlePreview(row)">预览</el-button>
          <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="loadData"
        @current-change="loadData"
      />
    </div>

    <!-- 编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑模板' : '新建模板'" width="900px" destroy-on-close>
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="模板名称" prop="name">
              <el-input v-model="formData.name" placeholder="请输入模板名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="模板代码" prop="code">
              <el-input v-model="formData.code" placeholder="如 debt_confirm" :disabled="isEdit" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="签名服务商" prop="signProvider">
              <el-select v-model="formData.signProvider" style="width: 100%">
                <el-option label="富福德正网签系统" value="self" />
                <el-option label="腾讯电子签" value="tencent" />
                <el-option label="法大大" value="fadada" />
                <el-option label="e签宝" value="esign" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序">
              <el-input-number v-model="formData.sort" :min="0" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="模板描述">
          <el-input v-model="formData.description" type="textarea" :rows="2" placeholder="模板用途说明" />
        </el-form-item>
        <el-form-item label="模板变量">
          <el-input v-model="formData.variables" placeholder='JSON格式，如 ["debtorName","creditorName","amount"]' />
        </el-form-item>
        <el-form-item label="模板内容" prop="content">
          <el-input v-model="formData.content" type="textarea" :rows="15" placeholder="HTML模板内容，变量用{{变量名}}表示" />
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch v-model="formData.isActive" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </template>
    </el-dialog>

    <!-- 预览对话框 -->
    <el-dialog v-model="previewVisible" title="模板预览" width="800px">
      <div class="template-preview" v-html="previewContent"></div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import contractApi from '@/api/modules/contract'

// 签名服务商映射
const signProviderMap = {
  self: '富福德正',
  tencent: '腾讯电子签',
  fadada: '法大大',
  esign: 'e签宝'
}

// 状态
const loading = ref(false)
const submitLoading = ref(false)
const dialogVisible = ref(false)
const previewVisible = ref(false)
const isEdit = ref(false)
const tableData = ref([])
const previewContent = ref('')
const formRef = ref(null)

// 分页
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 搜索参数
const searchParams = reactive({
  keyword: '',
  isActive: null
})

// 表单数据
const formData = reactive({
  id: null,
  name: '',
  code: '',
  content: '',
  variables: '',
  description: '',
  isActive: true,
  sort: 0,
  signProvider: 'self'
})

// 表单验证规则
const rules = {
  name: [{ required: true, message: '请输入模板名称', trigger: 'blur' }],
  code: [{ required: true, message: '请输入模板代码', trigger: 'blur' }],
  content: [{ required: true, message: '请输入模板内容', trigger: 'blur' }]
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const res = await contractApi.getTemplateList({
      page: pagination.page,
      limit: pagination.limit,
      ...searchParams
    })
    tableData.value = res.list || []
    pagination.total = res.total || 0
  } catch (error) {
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// 重置搜索
const resetSearch = () => {
  searchParams.keyword = ''
  searchParams.isActive = null
  loadData()
}

// 新增
const handleAdd = () => {
  isEdit.value = false
  Object.assign(formData, {
    id: null,
    name: '',
    code: '',
    content: '',
    variables: '',
    description: '',
    isActive: true,
    sort: 0,
    signProvider: 'self'
  })
  dialogVisible.value = true
}

// 编辑
const handleEdit = (row) => {
  isEdit.value = true
  Object.assign(formData, {
    id: row.id,
    name: row.name,
    code: row.code,
    content: row.content,
    variables: row.variables,
    description: row.description,
    isActive: row.isActive,
    sort: row.sort,
    signProvider: row.signProvider || 'self'
  })
  dialogVisible.value = true
}

// 预览
const handlePreview = (row) => {
  let content = row.content
  try {
    const vars = JSON.parse(row.variables || '[]')
    vars.forEach(v => {
      content = content.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), `[${v}]`)
    })
  } catch (e) {}
  previewContent.value = content
  previewVisible.value = true
}

// 状态切换
const handleStatusChange = async (row) => {
  try {
    await contractApi.updateTemplate(row.id, { isActive: row.isActive })
    ElMessage.success('状态更新成功')
  } catch (error) {
    row.isActive = !row.isActive
    ElMessage.error('状态更新失败')
  }
}

// 删除
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定删除该模板吗？', '提示', { type: 'warning' })
    await contractApi.deleteTemplate(row.id)
    ElMessage.success('删除成功')
    loadData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

// 提交表单
const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    submitLoading.value = true
    if (isEdit.value) {
      await contractApi.updateTemplate(formData.id, formData)
      ElMessage.success('更新成功')
    } else {
      await contractApi.createTemplate(formData)
      ElMessage.success('创建成功')
    }
    dialogVisible.value = false
    loadData()
  } catch (error) {
    if (error !== false) {
      ElMessage.error(isEdit.value ? '更新失败' : '创建失败')
    }
  } finally {
    submitLoading.value = false
  }
}

// 格式化日期
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString('zh-CN')
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.contract-template-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}

.search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.template-preview {
  padding: 20px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
  max-height: 500px;
  overflow-y: auto;
}
</style>
